import { type ParentComponent, createContext, useContext } from "solid-js";
import type { PromptBarContextType, PromptBarProps } from "../PromptBar.types";
import { useCreditsDisplay, useKnowledgeSourcesDisplay, usePromptInput, usePromptMode } from "./hooks";
import { useWire } from "~/wire";
import { createPromptEditor, useMentionsState } from "@storytell/rich-text";
import { VariableV1EditorRenderer } from "./components/VariableV1Renderer/VariableV1Renderer";
import type { RichText } from "@storytell/cp-client";

const PromptBarContext = createContext<PromptBarContextType>();

export const PromptBarProvider: ParentComponent<PromptBarProps> = (props) => {
  const input = usePromptInput();
  const mode = usePromptMode();
  const credits = useCreditsDisplay(props.streamingState?.accumulatedMetrics);
  const knowledgeSources = useKnowledgeSourcesDisplay();
  const wire = useWire();

  const handleSubmit = (text: RichText.RichText) => {
    props.onSubmit({
      text,
    });
  };

  const mentionsState = useMentionsState({
    // TODO: @andi: limit how many we're returning here
    getMentionItems: async (query) => {
      if (query === "") {
        return [
          ...wire.sync.concepts
            .getAll()
            .map((concept) => ({ type: "concept" as const, id: concept.id, data: concept, text: concept.name })),
          ...wire.sync.assets
            .getAll()
            .map((asset) => ({ type: "asset" as const, id: asset.id, data: asset, text: asset.data.originalFilename })),
        ];
      }
      const assets = wire.sync.assets.fuzzy.search(query || "").map((result) => ({
        type: "asset" as const,
        id: result.item.id,
        data: result.item,
        text: result.item.data.originalFilename,
      }));
      const concepts = wire.sync.concepts.fuzzy.search(query || "").map((result) => ({
        type: "concept" as const,
        id: result.item.id,
        data: result.item,
        text: result.item.name,
      }));
      return [...concepts, ...assets];
    },
  });

  const promptEditor = createPromptEditor({
    autofocus: () => true,
    mentionsState,
    variableV1Props: {
      renderer: VariableV1EditorRenderer,
    },
    placeholder: () => "What are you trying to accomplish?",
    onSubmit: (text) => {
      handleSubmit(text);
    },
    onInput: (text) => {
      input.setInputValue(text);
    },
  });

  const contextValue: PromptBarContextType = {
    input: {
      inputValue: input.inputValue,
      setInputValue: input.setInputValue,
      characterCount: input.characterCount,
      lineCount: input.lineCount,
      hasInputText: input.hasInputText,
    },
    prompt: promptEditor,
    mode: {
      selectedMode: mode.selectedMode,
      setSelectedMode: mode.setSelectedMode,
      placeholderText: mode.placeholderText,
    },
    credits: {
      showTooltip: credits.showTooltip,
      showPricingModal: credits.showPricingModal,
      setShowTooltip: credits.setShowTooltip,
      toggleTooltip: credits.toggleTooltip,
      hideTooltip: credits.hideTooltip,
      openPricingModal: credits.openPricingModal,
      closePricingModal: credits.closePricingModal,
      credits: credits.credits,
      dailyCredits: credits.dailyCredits,
      planType: credits.planType,
      totalCredits: credits.totalCredits,
    },
    knowledgeSources: {
      showTooltip: knowledgeSources.showTooltip,
      training: knowledgeSources.training,
      web: knowledgeSources.web,
      myKnowledge: knowledgeSources.myKnowledge,
      memories: knowledgeSources.memories,
      selectedModel: knowledgeSources.selectedModel,
      setShowTooltip: knowledgeSources.setShowTooltip,
      toggleTooltip: knowledgeSources.toggleTooltip,
      hideTooltip: knowledgeSources.hideTooltip,
      toggleTraining: knowledgeSources.toggleTraining,
      toggleWeb: knowledgeSources.toggleWeb,
      toggleMyKnowledge: knowledgeSources.toggleMyKnowledge,
      toggleMemories: knowledgeSources.toggleMemories,
      setSelectedModel: knowledgeSources.setSelectedModel,
    },
    submission: {
      handleSubmit,
    },
    streaming: props.streamingState,
  };

  return <PromptBarContext.Provider value={contextValue}>{props.children}</PromptBarContext.Provider>;
};

export const usePromptBarContext = (): PromptBarContextType => {
  const context = useContext(PromptBarContext);
  if (!context) {
    throw new Error("usePromptBarContext must be used within a PromptBarProvider");
  }
  return context;
};
